import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../store/auth/selectors";
import {
  Modal,
  Descriptions,
  Tag,
  Space,
  Button,
  Form,
  Input,
  DatePicker,
  Upload,
  Popconfirm,
  Spin,
  Empty,
  Collapse,
  Timeline,
  Typography,
  message,
} from "antd";
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  StopOutlined,
  PlusOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  obtenerNovedad,
  actualizarNovedad,
  cambiarEstadoNovedad,
  eliminarNovedad,
  agregarSoporte,
  reemplazarSoporte,
  eliminarSoporte,
  listarAuditoriaNovedad,
} from "../../services/novedad_participante/novedadParticipanteService";
import SoporteItem from "./SoporteItem";

const { TextArea } = Input;
const { Text } = Typography;

const ESTADO_COLOR = { ACTIVA: "success", RESUELTA: "blue", ANULADA: "default" };

const ACCION_LABEL = { CREAR: "creó", ACTUALIZAR: "actualizó", ELIMINAR: "eliminó" };
const TABLA_LABEL = { novedad_participantes: "la novedad", novedad_soporte: "un soporte" };

/**
 * "Ver novedad": detalle editable, cambio de estado, soportes gestionables
 * y pestaña de historial (novedad_auditoria). Se usa tanto embebido desde
 * /reporteRIT como desde el listado standalone /novedad_participante.
 */
const NovedadDetalleModal = ({ open, onClose, novedadId, onCambio }) => {
  const permisos = useSelector((state) => selectAppPermissions(state, "novedad_participante"));
  const puedeEditar    = permisos.priv_update === "Y";
  const puedeEliminar  = permisos.priv_delete === "Y";
  const puedeAgregar   = permisos.priv_insert === "Y";
  const puedeVerSoporte = permisos.priv_print === "Y";

  const [form] = Form.useForm();
  const [novedad, setNovedad] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [subiendoSoporte, setSubiendoSoporte] = useState(false);
  const [auditoria, setAuditoria] = useState([]);
  const [cargandoAuditoria, setCargandoAuditoria] = useState(false);

  const cargar = useCallback(async () => {
    if (!novedadId) return;
    setCargando(true);
    try {
      const data = await obtenerNovedad(novedadId);
      setNovedad(data);
    } catch (error) {
      console.error(error);
      message.error("No se pudo cargar la novedad");
    } finally {
      setCargando(false);
    }
  }, [novedadId]);

  useEffect(() => {
    if (open) {
      setEditando(false);
      cargar();
    } else {
      setNovedad(null);
      setAuditoria([]);
    }
  }, [open, cargar]);

  const iniciarEdicion = () => {
    form.setFieldsValue({
      tipo_novedad: novedad.tipo_novedad,
      descripcion: novedad.descripcion,
      fecha_novedad: dayjs(novedad.fecha_novedad),
    });
    setEditando(true);
  };

  const guardarEdicion = async () => {
    try {
      const valores = await form.validateFields();
      setGuardando(true);
      await actualizarNovedad(novedad.id, {
        tipo_novedad: valores.tipo_novedad,
        descripcion: valores.descripcion,
        fecha_novedad: valores.fecha_novedad.format("YYYY-MM-DD"),
      });
      message.success("Novedad actualizada");
      setEditando(false);
      await cargar();
      onCambio?.();
    } catch (error) {
      if (error?.errorFields) return;
      console.error(error);
      message.error("Error al actualizar la novedad");
    } finally {
      setGuardando(false);
    }
  };

  const cambiarEstado = async (estado) => {
    try {
      setGuardando(true);
      await cambiarEstadoNovedad(novedad.id, estado);
      message.success(estado === "RESUELTA" ? "Novedad marcada como resuelta" : "Novedad anulada");
      await cargar();
      onCambio?.();
    } catch (error) {
      console.error(error);
      message.error("Error al cambiar el estado");
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    try {
      setGuardando(true);
      await eliminarNovedad(novedad.id);
      message.success("Novedad eliminada");
      onCambio?.();
      onClose();
    } catch (error) {
      console.error(error);
      message.error("Error al eliminar la novedad");
    } finally {
      setGuardando(false);
    }
  };

  const handleAgregarSoporte = async (file) => {
    setSubiendoSoporte(true);
    try {
      const formData = new FormData();
      formData.append("soportes", file);
      await agregarSoporte(novedad.id, formData);
      message.success("Soporte agregado");
      await cargar();
    } catch (error) {
      console.error(error);
      message.error("Error al agregar el soporte");
    } finally {
      setSubiendoSoporte(false);
    }
  };

  const handleReemplazarSoporte = async (soporteId, file) => {
    setSubiendoSoporte(true);
    try {
      const formData = new FormData();
      formData.append("soporte", file);
      await reemplazarSoporte(soporteId, formData);
      message.success("Soporte reemplazado");
      await cargar();
    } catch (error) {
      console.error(error);
      message.error("Error al reemplazar el soporte");
    } finally {
      setSubiendoSoporte(false);
    }
  };

  const handleEliminarSoporte = async (soporteId) => {
    setSubiendoSoporte(true);
    try {
      await eliminarSoporte(soporteId);
      message.success("Soporte eliminado");
      await cargar();
    } catch (error) {
      console.error(error);
      message.error("Error al eliminar el soporte");
    } finally {
      setSubiendoSoporte(false);
    }
  };

  const cargarAuditoria = async () => {
    if (!novedad || auditoria.length) return;
    setCargandoAuditoria(true);
    try {
      const data = await listarAuditoriaNovedad(novedad.id);
      setAuditoria(data);
    } catch (error) {
      console.error(error);
    } finally {
      setCargandoAuditoria(false);
    }
  };

  const uploadNuevoProps = {
    showUploadList: false,
    multiple: true,
    beforeUpload: (file) => {
      handleAgregarSoporte(file);
      return false;
    },
  };

  const esActiva = novedad?.estado === "ACTIVA";

  return (
    <Modal open={open} onCancel={onClose} title="Novedad" width={680} footer={null} destroyOnClose>
      {cargando || !novedad ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Municipio">{novedad.municipio_nombre || "—"}</Descriptions.Item>
            <Descriptions.Item label="Documento">{novedad.documento_identificacion}</Descriptions.Item>
            <Descriptions.Item label="Nombre">{novedad.nombre_completo || "—"}</Descriptions.Item>
            <Descriptions.Item label="Estado">
              <Tag color={ESTADO_COLOR[novedad.estado]}>{novedad.estado}</Tag>
            </Descriptions.Item>

            {editando ? (
              <>
                <Descriptions.Item label="Tipo de novedad">
                  <Form form={form} component={false}>
                    <Form.Item
                      name="tipo_novedad"
                      style={{ margin: 0 }}
                      rules={[{ required: true, message: "Requerido" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Form>
                </Descriptions.Item>
                <Descriptions.Item label="Descripción">
                  <Form form={form} component={false}>
                    <Form.Item
                      name="descripcion"
                      style={{ margin: 0 }}
                      rules={[{ required: true, message: "Requerido" }]}
                    >
                      <TextArea rows={3} />
                    </Form.Item>
                  </Form>
                </Descriptions.Item>
                <Descriptions.Item label="Fecha">
                  <Form form={form} component={false}>
                    <Form.Item
                      name="fecha_novedad"
                      style={{ margin: 0 }}
                      rules={[{ required: true, message: "Requerido" }]}
                    >
                      <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                    </Form.Item>
                  </Form>
                </Descriptions.Item>
              </>
            ) : (
              <>
                <Descriptions.Item label="Tipo de novedad">{novedad.tipo_novedad}</Descriptions.Item>
                <Descriptions.Item label="Descripción">{novedad.descripcion}</Descriptions.Item>
                <Descriptions.Item label="Fecha">{novedad.fecha_novedad?.slice(0, 10)}</Descriptions.Item>
              </>
            )}
          </Descriptions>

          <Space style={{ marginBottom: 16 }} wrap>
            {puedeEditar && !editando && (
              <Button icon={<EditOutlined />} onClick={iniciarEdicion}>
                Editar
              </Button>
            )}
            {puedeEditar && editando && (
              <>
                <Button type="primary" icon={<SaveOutlined />} loading={guardando} onClick={guardarEdicion}>
                  Guardar
                </Button>
                <Button icon={<CloseOutlined />} onClick={() => setEditando(false)}>
                  Cancelar
                </Button>
              </>
            )}

            {puedeEditar && esActiva && (
              <Popconfirm title="¿Marcar como resuelta?" onConfirm={() => cambiarEstado("RESUELTA")}>
                <Button icon={<CheckCircleOutlined />} loading={guardando}>
                  Resolver
                </Button>
              </Popconfirm>
            )}
            {puedeEditar && esActiva && (
              <Popconfirm title="¿Anular esta novedad?" onConfirm={() => cambiarEstado("ANULADA")}>
                <Button icon={<StopOutlined />} loading={guardando}>
                  Anular
                </Button>
              </Popconfirm>
            )}

            {puedeEliminar && (
              <Popconfirm title="¿Eliminar esta novedad y sus soportes?" onConfirm={eliminar}>
                <Button danger icon={<DeleteOutlined />} loading={guardando}>
                  Eliminar
                </Button>
              </Popconfirm>
            )}
          </Space>

          <div style={{ marginBottom: 8, fontWeight: 600 }}>Soportes</div>
          {novedad.soportes?.length ? (
            novedad.soportes.map((s) => (
              <SoporteItem
                key={s.id}
                soporte={s}
                cargando={subiendoSoporte}
                permitirVer={puedeVerSoporte}
                permitirReemplazar={puedeEditar}
                permitirEliminar={puedeEliminar}
                onReemplazar={(file) => handleReemplazarSoporte(s.id, file)}
                onEliminar={() => handleEliminarSoporte(s.id)}
              />
            ))
          ) : (
            <Empty description="Sin soportes" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: "16px 0" }} />
          )}

          {puedeAgregar && (
            <Upload {...uploadNuevoProps}>
              <Button icon={<PlusOutlined />} loading={subiendoSoporte} style={{ marginBottom: 16 }}>
                Agregar soporte
              </Button>
            </Upload>
          )}

          <Collapse
            ghost
            onChange={(keys) => keys.length && cargarAuditoria()}
            items={[
              {
                key: "historial",
                label: (
                  <Space>
                    <HistoryOutlined /> Historial de cambios
                  </Space>
                ),
                children: cargandoAuditoria ? (
                  <Spin size="small" />
                ) : auditoria.length ? (
                  <Timeline
                    items={auditoria.map((a) => ({
                      children: (
                        <>
                          <Text strong>{a.usuario_nombre || "usuario"}</Text>{" "}
                          {ACCION_LABEL[a.accion] || a.accion} {TABLA_LABEL[a.tabla_afectada] || ""}
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {new Date(a.fecha).toLocaleString("es-CO")}
                          </Text>
                        </>
                      ),
                    }))}
                  />
                ) : (
                  <Empty description="Sin movimientos" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ),
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
};

export default NovedadDetalleModal;
